import { InteractionType } from "@azure/msal-browser";
import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";
import { FC, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { msalApp } from "../context/authContext";
import { MileageRemindersProvider } from "../context/mileageRemindersContext";
import { BackdropProvider } from "../context/modalContext";
import NotificationDispatch, { useNotification } from "../context/notificationContext";
import UserSettingsProvider, { useUserSettings } from "../context/userSettingsContext";
import { I18nProvider, useI18n } from "../hooks/useTranslations";
import styles from "./App.module.css";
import CarDrivers from "./carDrivers/CarDrivers";
import CarLeasings from "./carLeasings/CarLeasings";
import CarPlannings from "./carPlannings/CarPlannings";
import EditCarPlanning from "./carPlannings/EditCarPlanning";
import NewCarPlanning from "./carPlannings/NewCarPlanning";
import CarPlates from "./carPlates/CarPlates";
import CarPurchases from "./carPurchases/CarPurchases";
import EditCarPurchase from "./carPurchases/EditCarPurchase";
import NewCarPurchase from "./carPurchases/NewCarPurchase";
import Cars from "./cars/Cars";
import EditCar from "./cars/EditCar";
import MyCarOverview from "./cars/MyCarOverview";
import MyCars from "./cars/MyCars";
import NewCar from "./cars/NewCar";
import CarTasks from "./carTask/CarTasks";
import Claims from "./claims/Claims";
import EditClaim from "./claims/EditClaim";
import NewClaim from "./claims/NewClaim";
import { EditAdminClient, NewAdminClient } from "./clients/AdminClient";
import AdminClients from "./clients/AdminClients";
import CostImports from "./costImports/CostImports";
import TabsDkv from "./costImports/dkv/TabsDkv";
import TabsDkvTolls from "./costImports/dkvTolls/TabHighwayTolls";
import Costs from "./costs/Costs";
import DueDates from "./dueDates/DueDates";
import EditFine from "./fines/EditFine";
import Fines from "./fines/Fines";
import NewFine from "./fines/NewFine";
import HighwayPasses from "./highwayPasses/HighwayPasses";
import EditLeasingCompany from "./leasingCompanies/EditLeasingCompany";
import LeasingCompanies from "./leasingCompanies/LeasingCompanies";
import Maintenances from "./maintenances/Maintenances";
import Manufacturers from "./manufacturers/Manufacturers";
import Mileages from "./mileages/Mileages";
import OtherCarAssignments from "./otherCarAssignment/OtherCarAssignment";
import RentalCars from "./rentalCars/RentalCars";
import EditRole from "./roles/EditRole";
import NewRole from "./roles/NewRole";
import Roles from "./roles/Roles";
import EditSubsidiary from "./subsidiaries/EditSubsidiary";
import NewSubsidiary from "./subsidiaries/NewSubsidiary";
import Subsidiaries from "./subsidiaries/Subsidiaries";
import SubsidiaryVatCodes from "./subsidiaryVatCodes/SubsidiaryVatCodes";
import EditSupplier from "./suppliers/EditSupplierDialog";
import Suppliers from "./suppliers/Suppliers";
import ErrorText from "./ui/ErrorText";
import Header from "./ui/Header";
import LoadingSpinner from "./ui/LoadingSpinner";
import Menu from "./ui/menu/Menu";
import NotAllowedPage from "./ui/NotAllowedPage";
import Notification from "./ui/Notification";
import EditUser from "./users/EditUser";
import MyProfile from "./users/MyProfile";
import NewUser from "./users/NewUser";
import Users from "./users/Users";

const Spinner = () => (
  <div className={styles.spinner}>
    <LoadingSpinner />
  </div>
);

const App: FC = () => (
  <MsalProvider instance={msalApp}>
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      loadingComponent={Spinner}
      authenticationRequest={{ redirectUri: window.location.origin }}
    >
      <BrowserRouter>
        <UserSettingsProvider>
          <I18nProvider>
            <AppWithLocale />
          </I18nProvider>
        </UserSettingsProvider>
      </BrowserRouter>
    </MsalAuthenticationTemplate>
  </MsalProvider>
);

export default App;

const AppWithLocale: FC = () => {
  useI18n();

  return <AppRoutes />;
};

const AppRoutes: FC = () => {
  const [{ show, severity, message, sticky }, dispatch] = useNotification();
  const userSettings = useUserSettings();
  const i18n = useI18n();

  useEffect(() => {
    // if it is the staging environment the document.titel is changed accordingly
    if (import.meta.env.MODE === "staging" && !document.title.includes(i18n.t.common.testEnvironment)) {
      document.title += " - " + i18n.t.common.testEnvironment;
    }
  }, []);

  return !userSettings || userSettings?.isLoading ? (
    <div className={styles.loadingSpinner}>
      <LoadingSpinner />
    </div>
  ) : (
    <BackdropProvider>
      <NotificationDispatch.Provider value={dispatch}>
        <MileageRemindersProvider>
          <div className={styles.menu}>
            <Menu />
          </div>
          <div className={styles.content}>
            {userSettings.error ? (
              <>
                <Header title={i18n.t.common.error} />
                <ErrorText text={i18n.t.common.genericError} />
              </>
            ) : (
              <Routes>
                <Route index element={userSettings.permissions.cars.update ? <Cars /> : <MyCars />} />

                <Route path="my_cars" element={<MyCars />} />
                <Route path="me" element={<MyProfile />} />
                <Route path="my_cars/:carId/:carDriverId" element={<MyCarOverview />} />
                {!!userSettings?.isManager && (
                  <>
                    <Route path="my_team_cars" element={<MyCars isTeam />} />
                    <Route path="my_team_cars/:carId/:carDriverId" element={<MyCarOverview isTeam />} />
                  </>
                )}

                {!!userSettings.permissions.leasingCompanies.read ? (
                  <>
                    <Route path="admin/leasing_companies" element={<LeasingCompanies />} />
                    <Route path="admin/leasing_companies/:id" element={<EditLeasingCompany />} />
                  </>
                ) : (
                  <Route path="admin/leasing_companies" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.subsidiaries.read ? (
                  <Route path="admin/subsidiaries" element={<Subsidiaries />} />
                ) : (
                  <Route path="admin/subsidiaries" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.subsidiaries.insert ? (
                  <Route path="admin/subsidiaries/new" element={<NewSubsidiary />} />
                ) : (
                  <Route path="admin/subsidiaries/new" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.subsidiaries.read && (
                  <Route path="admin/subsidiaries/:id" element={<EditSubsidiary />} />
                )}

                {!!userSettings.permissions.cars.read ? (
                  <Route path="admin/subsidiaries/:subsidiaryId/cars" element={<Cars />} />
                ) : (
                  <Route path="admin/subsidiaries/:subsidiaryId/cars" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.subsidiaryVatCodes.read ? (
                  <Route path="admin/subsidiaries/:subsidiaryId/vat_codes" element={<SubsidiaryVatCodes />} />
                ) : (
                  <Route path="admin/subsidiaries/:subsidiaryId/vat_codes" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.suppliers.read ? (
                  <>
                    <Route path="admin/suppliers" element={<Suppliers />} />
                    <Route path="admin/suppliers/:id" element={<EditSupplier />} />
                  </>
                ) : (
                  <Route path="admin/suppliers" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.manufacturers.read ? (
                  <Route path="admin/manufacturers" element={<Manufacturers />} />
                ) : (
                  <Route path="admin/manufacturers" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.cars.read ? (
                  <Route path="admin/cars" element={<Cars />} />
                ) : (
                  <Route path="admin/cars" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.cars.insert ? (
                  <Route path="admin/cars/new" element={<NewCar />} />
                ) : (
                  <Route path="admin/cars/new" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.cars.read && <Route path="admin/cars/:id" element={<EditCar />} />}

                {!!userSettings.permissions.costs.read ? (
                  <Route path="admin/costs" element={<Costs />} />
                ) : (
                  <Route path="admin/costs" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.carPurchases.read ? (
                  <Route path="admin/car_purchases" element={<CarPurchases />} />
                ) : (
                  <Route path="admin/car_purchases" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.carPurchases.read ? (
                  <Route path="admin/car_purchases/arriving_soon" element={<CarPurchases isArrivingSoon />} />
                ) : (
                  <Route path="admin/car_purchases" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.carPurchases.insert ? (
                  <Route path="admin/car_purchases/new" element={<NewCarPurchase />} />
                ) : (
                  <Route path="admin/car_purchases/new" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.carPurchases.read && (
                  <Route path="admin/car_purchases/:id" element={<EditCarPurchase />} />
                )}

                {!!userSettings.permissions.carPlates.read ? (
                  <Route path="admin/car_plates" element={<CarPlates />} />
                ) : (
                  <Route path="admin/car_plates" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.carDrivers.read ? (
                  <Route path="admin/car_drivers" element={<CarDrivers />} />
                ) : (
                  <Route path="admin/car_drivers" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.highwayPasses.read ? (
                  <Route path="admin/highway_passes" element={<HighwayPasses />} />
                ) : (
                  <Route path="admin/highway_passes" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.mileages.read ? (
                  <Route path="admin/mileages" element={<Mileages />} />
                ) : (
                  <Route path="admin/mileages" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.maintenances.read ? (
                  <Route path="admin/maintenances" element={<Maintenances />} />
                ) : (
                  <Route path="admin/maintenances" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.costsImports.read ? (
                  <>
                    <Route path="admin/costs_imports" element={<CostImports />} />
                    <Route path="admin/costs_imports/dkv/:id" element={<TabsDkv />} />
                    <Route path="admin/costs_imports/dkv/:id/:withErrors" element={<TabsDkv />} />
                    <Route path="admin/costs_imports/tolls/:id" element={<TabsDkvTolls />} />
                    <Route path="admin/costs_imports/tolls/:id/:withErrors" element={<TabsDkvTolls />} />
                  </>
                ) : (
                  <Route path="admin/costs_imports" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.dueDates.read ? (
                  <>
                    <Route path="admin/due_dates" element={<DueDates />} />
                    <Route path="admin/due_dates/not_settled" element={<DueDates isSettled={false} />} />
                    <Route path="admin/due_dates/settled" element={<DueDates isSettled />} />
                    <Route path="admin/due_dates/expiring_soon" element={<DueDates isExpiringSoon />} />
                    <Route path="admin/due_dates/expired" element={<DueDates isExpired />} />
                  </>
                ) : (
                  <Route path="admin/due_dates" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.claims.read ? (
                  <Route path="admin/claims" element={<Claims />} />
                ) : (
                  <Route path="admin/claims" element={<NotAllowedPage />} />
                )}
                {!!userSettings.permissions.claims.read && <Route path="admin/claims/:id" element={<EditClaim />} />}

                {!!userSettings.permissions.claims.insert ? (
                  <Route path="admin/claims/new" element={<NewClaim />} />
                ) : (
                  <Route path="admin/claims/new" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.fines.read ? (
                  <Route path="admin/fines" element={<Fines />} />
                ) : (
                  <Route path="admin/fines" element={<NotAllowedPage />} />
                )}
                {!!userSettings.permissions.fines.read && <Route path="admin/fines/:id" element={<EditFine />} />}

                {!!userSettings.permissions.fines.insert ? (
                  <Route path="admin/fines/new" element={<NewFine />} />
                ) : (
                  <Route path="admin/fines/new" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.otherCarAssignment.read ? (
                  <Route path="admin/other_car_assignments" element={<OtherCarAssignments />} />
                ) : (
                  <Route path="admin/other_car_assignments" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.carTasks.read ? (
                  <>
                    <Route path="admin/car_tasks" element={<CarTasks />} />
                    <Route path="admin/car_tasks/only_to_do" element={<CarTasks onlyToDo />} />
                  </>
                ) : (
                  <Route path="admin/car_tasks" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.carLeasings.read ? (
                  <>
                    <Route path="admin/car_leasings" element={<CarLeasings />} />
                    <Route path="admin/car_leasings/issues" element={<CarLeasings notificationsOnly />} />
                  </>
                ) : (
                  <Route path="admin/car_leasings" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.users.read ? (
                  <Route path="admin/users" element={<Users />} />
                ) : (
                  <Route path="admin/users" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.users.insert ? (
                  <Route path="admin/users/new" element={<NewUser />} />
                ) : (
                  <Route path="admin/users/new" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.users.read && <Route path="admin/users/:id" element={<EditUser />} />}

                <Route path="admin/clients" element={<AdminClients />} />
                <Route path="admin/clients/new" element={<NewAdminClient />} />
                <Route path="admin/clients/:clientId" element={<EditAdminClient />} />

                {!!userSettings.permissions.subsidiaryVatCodes.read ? (
                  <Route path="admin/subsidiary_vat_codes" element={<SubsidiaryVatCodes />} />
                ) : (
                  <Route path="admin/subsidiary_vat_codes" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.roles.read ? (
                  <Route path="admin/roles" element={<Roles />} />
                ) : (
                  <Route path="admin/roles" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.roles.insert ? (
                  <Route path="admin/roles/new" element={<NewRole />} />
                ) : (
                  <Route path="admin/roles/new" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.carPlannings.read ? (
                  <Route path="admin/car_plannings" element={<CarPlannings />} />
                ) : (
                  <Route path="admin/car_plannings" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.carPlannings.insert ? (
                  <Route path="admin/car_plannings/new" element={<NewCarPlanning />} />
                ) : (
                  <Route path="admin/car_plannings/new" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.carPlannings.read ? (
                  <Route path="admin/car_plannings/:id" element={<EditCarPlanning />} />
                ) : (
                  <Route path="admin/car_plannings/:id" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.rentalCars.read ? (
                  <Route path="admin/rental_cars" element={<RentalCars />} />
                ) : (
                  <Route path="admin/rental_cars" element={<NotAllowedPage />} />
                )}

                {!!userSettings.permissions.roles.read && <Route path="admin/roles/:id" element={<EditRole />} />}

                <Route
                  path="*"
                  element={
                    <>
                      <Header title={i18n.t.common.notFound} />
                      <ErrorText text={i18n.t.common.resourceNotFound} />
                    </>
                  }
                />
              </Routes>
            )}
          </div>
          {show && <Notification type={severity} text={message} sticky={sticky} />}
        </MileageRemindersProvider>
      </NotificationDispatch.Provider>
    </BackdropProvider>
  );
};
